import React from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import styles from '../NewUserWizard.module.css';

/* ── Feature icons as named components (avoids babel confusion) ── */
const IconConnect = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const IconFetch = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const IconAI = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9219a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8" />
    <path d="M12 17v4" />
  </svg>
);

const IconApprove = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const StepWelcome = ({ userName, onNext }) => {
  const { t } = useLanguage();

  const features = [
    { bg: '#eff8ff', Icon: IconConnect, title: t('step1ConnectTitle', 'Connect Google'), desc: t('step1ConnectDesc', 'Link your Google Business Profile securely via OAuth in 30 seconds.') },
    { bg: '#f0fdf4', Icon: IconFetch,   title: t('step1FetchTitle', 'Fetch Reviews'), desc: t('step1FetchDesc', 'We pull your latest Google reviews every few hours automatically.') },
    { bg: '#faf5ff', Icon: IconAI,      title: t('step1AiDraftTitle', 'AI Drafts Replies'), desc: t('step1AiDraftDesc', 'Our AI crafts personalised, brand-matched replies for each review.') },
    { bg: '#fffbeb', Icon: IconApprove, title: t('step1ApproveTitle', 'You Approve'), desc: t('step1ApproveDesc', 'One-click email approval before any reply goes live on Google.') },
  ];

  const greetingName = userName ? `, ${userName}` : '';

  return (
    <div>
      <div className={styles.stepTag}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
        {t('wizardGettingStarted', 'Getting started')}
      </div>

      <h2 className={styles.wizardTitle}>
        {t('step1WelcomeTitle', 'Welcome to ReplyShoot{name}! 👋').replace('{name}', greetingName)}
      </h2>
      <p className={styles.wizardSubtitle}>
        {t('step1WelcomeSub', "Let's get you set up in under 2 minutes. Here's what we'll do together:")}
      </p>

      <div className={styles.featureGrid}>
        {features.map(({ bg, Icon, title, desc }, i) => (
          <div className={styles.featureCard} key={i}>
            <div className={styles.featureIconWrap} style={{ background: bg }}>
              <Icon />
            </div>
            <div className={styles.featureTitle}>{title}</div>
            <div className={styles.featureDesc}>{desc}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className={styles.btnPrimary} onClick={onNext}>
          {t('step1StartBtn', "Let's get started")}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default StepWelcome;
